import React, { useState, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Image,
  Icon,
  Container,
  Card,
  Wrap,
  WrapItem,
  Stack,
  Flex,
  Alert,
  Link,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { Tooltip } from '@/components/ui/tooltip';
import ProfilePreviewDisclaimer from './ProfilePreviewDisclaimer';
import ShareProfileButton from './ShareProfileButton.jsx';
import {
  FaMusic,
  FaMapMarkerAlt,
  FaUsers,
  FaCalendarAlt,
  FaStar,
  FaRegStar,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaInstagram,
  FaFacebook,
  FaYoutube,
  FaTiktok,
  FaPlay,
  FaShieldAlt,
  FaChurch,
  FaCheckCircle,
  FaClock,
  FaHandshake,
  FaAward,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const PublicProfilePreview = ({ isOpen, onClose, profileData, shareUrl = '' }) => {
  const bg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  /** Todas las variantes de color deben declararse aquí: no usar `useColorModeValue` dentro del Modal (rompe el orden de hooks si `isOpen` es false). */
  const modalBodyBg = useColorModeValue('gray.50', 'gray.900');
  const headingStrongColor = useColorModeValue('gray.800', 'white');
  const scrollbarTrackBg = useColorModeValue('gray.100', 'gray.700');
  const scrollbarThumbBg = useColorModeValue('gray.400', 'gray.500');
  const scrollbarThumbHoverBg = useColorModeValue('gray.500', 'gray.400');
  const repertorioRowOddBg = useColorModeValue('gray.50', 'gray.700');
  const ensayoCardBg = useColorModeValue('purple.50', 'gray.700');
  const ensayoNotasBg = useColorModeValue('white', 'gray.600');
  const eventoCardBg = useColorModeValue('gray.50', 'gray.700');
  const avalCardBg = useColorModeValue('green.50', 'green.900');
  const resenasResumenCardBg = useColorModeValue('purple.50', 'purple.900');
  
  // Estado para el modal de imagen
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  
  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc);
    setIsImageModalOpen(true);
  };
  
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Repertorio data loaded from profile

  // Función para obtener el estilo del fondo de la card
  const getCardBackgroundStyle = (imagenFondo) => {
    if (imagenFondo && imagenFondo.trim() !== '') {
      return {
        backgroundImage: `url(${imagenFondo})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    }
    return {
      background: 'linear-gradient(135deg, #805AD5 0%, #B794F6 50%, #F6AD55 100%)'
    };
  };

  // Función para generar URLs de redes sociales
  const getSocialUrl = (platform, value) => {
    if (!value) return null;
    
    // Si ya es una URL completa, devolverla
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }
    
    // Limpiar @ del inicio si existe
    let cleanValue = value.startsWith('@') ? value.substring(1) : value;
    
    // Limpiar prefijos de dominio si el usuario los puso sin protocolo (ej: www.facebook.com/usuario)
    cleanValue = cleanValue.replace(/^(www\.)?(instagram\.com|facebook\.com|youtube\.com|youtu\.be|tiktok\.com|x\.com|twitter\.com)\/?/i, '');
    
    switch (platform) {
      case 'instagram':
        return `https://instagram.com/${cleanValue}`;
      case 'facebook':
        return `https://facebook.com/${cleanValue}`;
      case 'youtube':
        // Si tiene @ al principio, es un handle de canal
        if (value.startsWith('@')) {
          return `https://youtube.com/@${cleanValue}`;
        }
        // Si parece una ruta de canal
        if (cleanValue.startsWith('c/') || cleanValue.startsWith('channel/') || cleanValue.startsWith('user/')) {
          return `https://youtube.com/${cleanValue}`;
        }
        // Si es solo un nombre, asumir que es un handle de canal
        return `https://youtube.com/@${cleanValue}`;
      case 'twitter':
        return `https://x.com/${cleanValue}`;
      case 'tiktok':
        return `https://tiktok.com/@${cleanValue}`;
      default:
        return null;
    }
  };

  // Función para extraer ID de video de YouTube
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };
  const isYouTubeShort = (url) => !!(url && /youtube\.com\/shorts\//.test(url));

  // Datos del perfil (sin datos mockeados)
  const bandData = {
    nombre: profileData?.nombre || '',
    tipo: profileData?.tipoAgrupacion || profileData?.tipoGrupo || '',
    descripcion: profileData?.bio || profileData?.descripcion || '',
    localidad: profileData?.localizacion?.ciudad || profileData?.localidad || '',
    provincia: profileData?.localizacion?.provincia || profileData?.provincia || '',
    numMusicos: profileData?.nMusicos || profileData?.numMusicos || '',
    anoFundacion: profileData?.anoFundacion || '',
    verified: profileData?.verificationStatus === 'VERIFIED',
    experiencia: profileData?.experiencia || '',
    avatar: profileData?.avatar || null,
    galeria: profileData?.fotos || profileData?.galeria || [],
    videos: profileData?.videos || [],
    contacto: {
      telefono: profileData?.contactoPublico?.telefono || profileData?.telefono || '',
      email: profileData?.contactoPublico?.email || profileData?.email || '',
      web: profileData?.contactoPublico?.website || profileData?.web || '',
    },
    redes: {
      instagram: profileData?.redesSociales?.instagram || '',
      facebook: profileData?.redesSociales?.facebook || '',
      youtube: profileData?.redesSociales?.youtube || '',
      twitter: profileData?.redesSociales?.twitter || '',
      tiktok: profileData?.redesSociales?.tiktok || '',
    },
    especialidades: profileData?.especialidades || [],
    repertorio: profileData?.repertorio && profileData.repertorio.length > 0
      ? profileData.repertorio
      : [],
    ensayos: {
      // Verificar si ensayos viene como objeto con horarios o como array directo
      publico: Array.isArray(profileData?.ensayos) 
        ? (profileData.ensayos.length > 0 && profileData.ensayos[0]?.publico) || false
        : profileData?.ensayos?.publico || false,
      horarios: Array.isArray(profileData?.ensayos)
        ? profileData.ensayos
        : (profileData?.ensayos?.horarios || [])
    },
    eventosPublicos: profileData?.eventosPublicos || [],
    confianza: {
      entidades: profileData?.entidadesConfianza || 0,
      listaEntidades: profileData?.listaEntidadesConfianza || []
    },
    resenas: (profileData?.resenas || []).filter(r => r.isPublished !== false),
    avales: profileData?.avales || profileData?.avalesRecibidos || [],
    stats: profileData?.stats || null,
  };

  // Calcular estadísticas de reseñas
  const calcularEstadisticasResenas = () => {
    const resenasPublicadas = bandData.resenas;
    if (!resenasPublicadas || resenasPublicadas.length === 0) {
      return { promedio: 0, total: 0 };
    }
    const suma = resenasPublicadas.reduce((acc, r) => acc + (r.ratingGeneral || r.rating || 0), 0);
    const promedio = suma / resenasPublicadas.length;
    return { promedio: promedio.toFixed(1), total: resenasPublicadas.length };
  };
  
  const estadisticasResenas = calcularEstadisticasResenas();

  // Estado para scroll infinito de entidades
  const [entidadesVisibles, setEntidadesVisibles] = useState(3);
  const [cargandoMas, setCargandoMas] = useState(false);

  const cargarMasEntidades = useCallback(() => {
    if (cargandoMas || entidadesVisibles >= bandData.confianza.listaEntidades.length) return;
    
    setCargandoMas(true);
    setTimeout(() => {
      setEntidadesVisibles(prev => Math.min(prev + 3, bandData.confianza.listaEntidades.length));
      setCargandoMas(false);
    }, 800); // Simula carga
  }, [cargandoMas, entidadesVisibles, bandData.confianza.listaEntidades.length]);

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header>
              <HStack>
                <Icon as={FaMusic} color="purple.600" />
                <Text>Vista Previa del Perfil Público</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />

            <Dialog.Body p={0}>
              <Box bg={modalBodyBg} minH="full">
                <Container maxW="4xl" py={6}>
                  <VStack gap={6} align="stretch">
                    
                    {/* Disclaimer */}
                    <ProfilePreviewDisclaimer profileType="banda" />
                    
                    {/* Header estilo LinkedIn/Facebook */}
                    <Box position="relative" w="full">
                      {/* Imagen de fondo */}
                      <Box
                        w="full"
                        h="320px"
                        rounded="lg"
                        overflow="hidden"
                        style={getCardBackgroundStyle(profileData?.fondoHeaderImagen)}
                        position="relative"
                      >
                        {/* Overlay sutil para mejor contraste */}
                        <Box
                          position="absolute"
                          top="0"
                          left="0"
                          right="0"
                          bottom="0"
                          bg="blackAlpha.200"
                        />
                      </Box>

                      {/* Caja blanca con la información */}
                      <Card.Root
                        mt="-20px"
                        rounded="2xl"
                        pt={6}
                        pb={6}
                        px={6}
                        bg={bg}
                        shadow="xl"
                        border="1px solid"
                        borderColor={borderColor}
                        position="relative"
                        zIndex="1"
                      >
                        <VStack gap={4} align="center" textAlign="center">
                          {/* Información Principal */}
                          <VStack gap={2}>
                            <HStack gap={4} align="center">
                              {/* Avatar a la izquierda del nombre */}
                              <Avatar.Root
                                size="xl"
                                border="4px solid white"
                                bg="purple.100"
                                shadow="lg"
                                cursor={bandData.avatar ? "pointer" : "default"}
                                onClick={() => bandData.avatar && handleImageClick(bandData.avatar)}
                                _hover={bandData.avatar ? { transform: 'scale(1.05)', transition: 'transform 0.2s' } : {}}><Avatar.Fallback name={bandData.nombre} /><Avatar.Image src={bandData.avatar} /></Avatar.Root>
                              <VStack gap={1} align="start">
                                <HStack gap={2}>
                                  <Heading size="xl" color={headingStrongColor}>
                                    {bandData.nombre}
                                  </Heading>
                                  {bandData.verified && (
                                    <Tooltip content="Perfil validado" showArrow>
                                      <span>
                                        <Icon as={FaCheckCircle} color="blue.500" w={6} h={6} />
                                      </span>
                                    </Tooltip>
                                  )}
                                </HStack>
                                <Text fontSize="lg" fontWeight="medium" color={textColor}>
                                  {bandData.tipo}
                                </Text>
                              </VStack>
                            </HStack>
                            <HStack gap={6} color={mutedColor} flexWrap="wrap" justify="center">
                              <HStack gap={1}>
                                <Icon as={FaMapMarkerAlt} />
                                <Text fontSize="sm">{bandData.localidad}, {bandData.provincia}</Text>
                              </HStack>
                              {bandData.numMusicos && (
                                <HStack gap={1}>
                                  <Icon as={FaUsers} />
                                  <Text fontSize="sm">{bandData.numMusicos} músicos</Text>
                                </HStack>
                              )}
                              {bandData.anoFundacion && (
                                <HStack gap={1}>
                                  <Icon as={FaCalendarAlt} />
                                  <Text fontSize="sm">Fundada en {bandData.anoFundacion}</Text>
                                </HStack>
                              )}
                            </HStack>
                          </VStack>

                          {/* Trust & Reputation Badges */}
                          <HStack gap={4} justify="center" flexWrap="wrap" py={2}>
                            {/* Avales count */}
                            {bandData.avales?.length > 0 && (
                              <HStack gap={2} bg="green.50" px={3} py={2} rounded="full">
                                <Icon as={FaHandshake} color="green.500" w={5} h={5} />
                                <Text fontSize="sm" fontWeight="bold" color="green.700">
                                  {bandData.avales.length} {bandData.avales.length === 1 ? 'aval' : 'avales'}
                                </Text>
                              </HStack>
                            )}
                            {/* Rating & Reviews */}
                            {estadisticasResenas.total > 0 && (
                              <HStack gap={2} bg="yellow.50" px={3} py={2} rounded="full">
                                <Icon as={FaStar} color="yellow.500" w={5} h={5} />
                                <Text fontSize="sm" fontWeight="bold" color="yellow.700">
                                  {estadisticasResenas.promedio}/5 ({estadisticasResenas.total})
                                </Text>
                              </HStack>
                            )}
                            {/* Gamification badges */}
                            {bandData.stats?.badges?.map((badge, index) => (
                              <Badge 
                                key={index} 
                                colorPalette={badge.level === 'gold' ? 'yellow' : badge.level === 'silver' ? 'gray' : 'orange'}
                                px={3} 
                                py={1} 
                                rounded="full"
                                fontSize="sm"
                              >
                                <Icon as={FaAward} mr={1} />
                                {badge.name}
                              </Badge>
                            ))}
                          </HStack>

                          <HStack gap={3} pt={2} flexWrap="wrap" justify="center">
                            <ShareProfileButton shareUrl={shareUrl} />
                          </HStack>
                        </VStack>
                      </Card.Root>
                    </Box>

                    {/* Acerca de */}
                    <Card.Root rounded="2xl">
                      <Card.Header>
                        <Heading size="md">Acerca de</Heading>
                      </Card.Header>
                      <Card.Body pt={0}>
                        <Text lineHeight="tall" color={textColor}>
                          {bandData.descripcion}
                        </Text>
                      </Card.Body>
                    </Card.Root>

                    {/* Especialidades */}
                    <Card.Root rounded="2xl">
                      <Card.Header>
                        <Heading size="md">Especialidades</Heading>
                      </Card.Header>
                      <Card.Body pt={0}>
                        {bandData.especialidades && bandData.especialidades.length > 0 ? (
                          <Wrap gap={2}>
                            {bandData.especialidades.map((especialidad, index) => (
                              <WrapItem key={index}>
                                <Badge 
                                  colorPalette="purple" 
                                  variant="subtle" 
                                  px={3} 
                                  py={1}
                                  rounded="full"
                                >
                                  {especialidad}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        ) : (
                          <Text color="gray.500" fontStyle="italic" fontSize="sm">
                            No hay especialidades añadidas
                          </Text>
                        )}
                      </Card.Body>
                    </Card.Root>

                    {/* Repertorio */}
                    {bandData.repertorio.length > 0 && (
                      <Card.Root rounded="2xl">
                        <Card.Header>
                          <HStack>
                            <Icon as={FaMusic} color="purple.500" />
                            <Heading size="md">Repertorio</Heading>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            {bandData.repertorio.length} {bandData.repertorio.length === 1 ? 'obra' : 'obras'} en el repertorio
                          </Text>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <Box 
                            border="1px solid" 
                            borderColor={borderColor} 
                            rounded="lg"
                            maxHeight="400px"
                            overflowY="auto"
                            css={{
                              '& &::-webkit-scrollbar': {
                                width: '8px',
                              },

                              '& &::-webkit-scrollbar-track': {
                                background: scrollbarTrackBg,
                                borderRadius: '10px',
                              },

                              '& &::-webkit-scrollbar-thumb': {
                                background: scrollbarThumbBg,
                                borderRadius: '10px',
                                '&:hover': {
                                  background: scrollbarThumbHoverBg,
                                },
                              }
                            }}
                          >
                      {bandData.repertorio.map((item, index) => {
                        const nombre = item.nombre || item.marcha?.nombre || '';
                        const autor = item.autor || item.marcha?.autor || '';
                        const tipo = item.tipo || item.marcha?.tipo || '';

                        return (
                          <Flex
                            key={item.id || index}
                            p={3}
                            align="center"
                            justify="space-between"
                            bg={index % 2 === 0 ? bg : repertorioRowOddBg}
                            borderBottom={index < bandData.repertorio.length - 1 ? '1px solid' : 'none'}
                            borderColor={borderColor}
                            transition="background 0.2s"
                          >
                            <HStack gap={3} flex={1}>
                              <Text 
                                fontSize="sm" 
                                fontWeight="500" 
                                color="gray.500" 
                                minW="30px"
                              >
                                {index + 1}.
                              </Text>
                              <VStack align="start" gap={0} flex={1}>
                                <HStack gap={2} flexWrap="wrap">
                                  <Text fontWeight="600" fontSize="sm">
                                    {nombre}
                                  </Text>
                                  {item.marcha?.verificada && (
                                    <Badge colorPalette="purple" fontSize="2xs">
                                      Validado
                                    </Badge>
                                  )}
                                </HStack>
                                <HStack gap={2} flexWrap="wrap">
                                  {autor && (
                                    <Text fontSize="xs" color="gray.500">
                                      {autor}
                                    </Text>
                                  )}
                                  {autor && tipo && (
                                    <Text fontSize="xs" color="gray.400">•</Text>
                                  )}
                                  {tipo && (
                                    <Text fontSize="xs" color="gray.500">
                                      {tipo}
                                    </Text>
                                  )}
                                </HStack>
                              </VStack>
                            </HStack>
                          </Flex>
                        );
                      })}
                          </Box>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Horarios de Ensayo */}
                    {bandData.ensayos && bandData.ensayos.publico && bandData.ensayos.horarios.length > 0 && (
                      <Card.Root rounded="2xl">
                        <Card.Header>
                          <HStack>
                            <Icon as={FaClock} color="purple.500" />
                            <Heading size="md">Horarios de Ensayo</Heading>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            Puedes asistir como oyente en nuestros ensayos
                          </Text>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={3} align="stretch">
                            {bandData.ensayos.horarios.map((ensayo, index) => (
                              <Box
                                key={index}
                                p={4}
                                bg={ensayoCardBg}
                                rounded="lg"
                                borderLeft="4px solid"
                                borderColor="purple.500"
                              >
                                <VStack align="start" gap={2}>
                                  <HStack gap={3} w="full" justify="space-between">
                                    <HStack gap={2}>
                                      <Icon as={FaCalendarAlt} color="purple.600" />
                                      <Text fontWeight="600" fontSize="md">
                                        {ensayo.diaSemana}
                                      </Text>
                                    </HStack>
                                    <Badge colorPalette="purple" fontSize="sm" px={3} py={1}>
                                      {ensayo.horaInicio} - {ensayo.horaFin}
                                    </Badge>
                                  </HStack>
                                  
                                  {ensayo.ubicacion && (
                                    <HStack gap={2}>
                                      <Icon as={FaMapMarkerAlt} color="gray.500" boxSize={3} />
                                      <Text fontSize="sm" color="gray.600">
                                        {ensayo.ubicacion}
                                      </Text>
                                    </HStack>
                                  )}
                                  
                                  {ensayo.notas && (
                                    <Box
                                      mt={2}
                                      p={2}
                                      bg={ensayoNotasBg}
                                      rounded="md"
                                      w="full"
                                    >
                                      <Text fontSize="xs" color="gray.600" fontStyle="italic">
                                        {ensayo.notas}
                                      </Text>
                                    </Box>
                                  )}
                                </VStack>
                              </Box>
                            ))}
                            
                            <Alert.Root status="info" rounded="lg" mt={2}>
                              <Alert.Indicator />
                              <VStack align="start" gap={0}>
                                <Text fontSize="sm" fontWeight="600">
                                  ¿Interesado en asistir?
                                </Text>
                                <Text fontSize="xs">
                                  Contacta con la banda para confirmar tu asistencia como oyente
                                </Text>
                              </VStack>
                            </Alert.Root>
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Próximos Eventos Públicos */}
                    {bandData.eventosPublicos && bandData.eventosPublicos.length > 0 && (
                      <Card.Root rounded="2xl">
                        <Card.Header>
                          <HStack>
                            <Icon as={FaCalendarAlt} color="purple.500" />
                            <Heading size="md">Próximos Eventos</Heading>
                          </HStack>
                          <Text fontSize="sm" color="gray.600" mt={1}>
                            Fechas en las que esta banda tiene eventos programados
                          </Text>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={3} align="stretch">
                            {bandData.eventosPublicos.map((evento, index) => {
                              const tipoEventoLabels = {
                                procesion: 'Procesión',
                                concierto: 'Concierto',
                                certamen: 'Certamen',
                                ensayo_especial: 'Ensayo especial',
                                otro: 'Otro evento',
                              };
                              return (
                                <Box
                                  key={index}
                                  p={4}
                                  bg={eventoCardBg}
                                  rounded="lg"
                                  borderLeft="4px solid"
                                  borderColor="purple.500"
                                >
                                  <HStack justify="space-between" w="full">
                                    <VStack align="start" gap={1}>
                                      <HStack gap={2}>
                                        <Icon as={FaCalendarAlt} color="purple.600" boxSize={4} />
                                        <Text fontWeight="600" fontSize="md">
                                          {new Date(evento.fecha).toLocaleDateString('es-ES', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                          })}
                                        </Text>
                                      </HStack>
                                      {(evento.notas || evento.evento) && (
                                        <Text fontSize="sm" color="gray.600" pl={6}>
                                          {evento.notas || evento.evento}
                                        </Text>
                                      )}
                                    </VStack>
                                    <Badge
                                      colorPalette={
                                        (evento.tipoEvento || evento.tipo) === 'procesion' ? 'purple' :
                                        (evento.tipoEvento || evento.tipo) === 'concierto' ? 'blue' :
                                        (evento.tipoEvento || evento.tipo) === 'certamen' ? 'orange' : 'gray'
                                      }
                                      fontSize="xs"
                                      px={3}
                                      py={1}
                                      rounded="full"
                                    >
                                      {tipoEventoLabels[evento.tipoEvento || evento.tipo] || evento.tipoEvento || evento.tipo || 'Evento'}
                                    </Badge>
                                  </HStack>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Información de Contacto */}
                    <Card.Root>
                      <Card.Header>
                        <Heading size="md">Información de contacto</Heading>
                      </Card.Header>
                      <Card.Body pt={0}>
                        <VStack gap={6} align="center">
                          <VStack align="stretch" gap={3} w="full">
                            <Text fontSize="sm" fontWeight="medium" textAlign="center">Síguenos en redes sociales</Text>
                            <HStack justify="center" gap={4} flexWrap="wrap">
                              {bandData.redes.instagram && (
                                <Link
                                  href={getSocialUrl('instagram', bandData.redes.instagram)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaInstagram} color="pink.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {bandData.redes.instagram}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {bandData.redes.facebook && (
                                <Link
                                  href={getSocialUrl('facebook', bandData.redes.facebook)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaFacebook} color="blue.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {bandData.redes.facebook}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {bandData.redes.youtube && (
                                <Link
                                  href={getSocialUrl('youtube', bandData.redes.youtube)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaYoutube} color="red.500" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {bandData.redes.youtube}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {bandData.redes.twitter && (
                                <Link
                                  href={getSocialUrl('twitter', bandData.redes.twitter)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaXTwitter} color="gray.800" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {bandData.redes.twitter}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                              {bandData.redes.tiktok && (
                                <Link
                                  href={getSocialUrl('tiktok', bandData.redes.tiktok)}
                                  _hover={{ textDecoration: 'none', opacity: 0.8 }}
                                  target='_blank'
                                  rel='noopener noreferrer'>
                                  <HStack gap={2} cursor="pointer">
                                    <Icon as={FaTiktok} color="gray.800" />
                                    <Text fontSize="sm" _hover={{ textDecoration: 'underline' }}>
                                      {bandData.redes.tiktok}
                                    </Text>
                                  </HStack>
                                </Link>
                              )}
                            </HStack>
                          </VStack>
                        </VStack>
                      </Card.Body>
                    </Card.Root>

                    {/* Galería de fotos */}
                    {bandData.galeria && bandData.galeria.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">Galería de fotos</Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                            {bandData.galeria.map((foto, index) => (
                              <Image
                                key={index}
                                src={foto}
                                alt={`Foto ${index + 1}`}
                                rounded="lg"
                                objectFit="cover"
                                h="120px"
                                w="full"
                                cursor="pointer"
                                onClick={() => handleImageClick(foto)}
                                _hover={{ 
                                  transform: 'scale(1.05)',
                                  shadow: 'lg'
                                }}
                                transition="all 0.2s"
                              />
                            ))}
                          </SimpleGrid>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Videos */}
                    {bandData.videos && bandData.videos.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaYoutube} mr={2} color="red.500" />
                            Videos
                          </Heading>
                        </Card.Header>
                        <Card.Body pt={0}>
                          {(() => {
                            const normalVideos = bandData.videos.filter(v => !isYouTubeShort(v.url));
                            const shorts       = bandData.videos.filter(v =>  isYouTubeShort(v.url));
                            const renderCard = (video, index) => {
                              const videoId = extractYouTubeId(video.url);
                              const short = isYouTubeShort(video.url);
                              return (
                                <Box key={video.id || index} rounded="lg" overflow="hidden" shadow="md" position="relative">
                                  {videoId ? (
                                    <Box position="relative" pb={short ? '177.78%' : '56.25%'}>
                                      <iframe
                                        src={`https://www.youtube.com/embed/${videoId}`}
                                        title={video.titulo}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                      />
                                      {short && (
                                        <Badge position="absolute" top={2} left={2} colorPalette="red" fontSize="2xs" rounded="md" px={1.5} zIndex={1} pointerEvents="none">Short</Badge>
                                      )}
                                    </Box>
                                  ) : (
                                    <Box h="150px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                      <Icon as={FaYoutube} w={12} h={12} color="gray.400" />
                                    </Box>
                                  )}
                                  <Box p={2} bg="white">
                                    <Text fontSize="sm" fontWeight="medium" isTruncated>{video.titulo}</Text>
                                  </Box>
                                </Box>
                              );
                            };
                            return (
                              <VStack gap={4} align="stretch">
                                {normalVideos.length > 0 && (
                                  <Box>
                                    {shorts.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Vídeos</Text>}
                                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                                      {normalVideos.map((v, i) => renderCard(v, i))}
                                    </SimpleGrid>
                                  </Box>
                                )}
                                {shorts.length > 0 && (
                                  <Box>
                                    {normalVideos.length > 0 && <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2} textTransform="uppercase">Shorts</Text>}
                                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                                      {shorts.map((v, i) => renderCard(v, i))}
                                    </SimpleGrid>
                                  </Box>
                                )}
                              </VStack>
                            );
                          })()}
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Avales de Confianza */}
                    {bandData.avales && bandData.avales.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <HStack justify="space-between" align="start">
                            <VStack align="start" gap={1}>
                              <Heading size="md">
                                <Icon as={FaHandshake} mr={2} color="green.500" />
                                Avales de Confianza
                              </Heading>
                              <Text fontSize="sm" color={textColor}>
                                Cofradías que avalan la trayectoria de esta banda
                              </Text>
                            </VStack>
                            <Badge colorPalette="green" fontSize="md" px={3} py={1} rounded="full">
                              {bandData.avales.length} {bandData.avales.length === 1 ? 'aval' : 'avales'}
                            </Badge>
                          </HStack>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={3} align="stretch">
                            {bandData.avales.slice(0, 5).map((aval) => (
                              <Card.Root key={aval.id} variant="outline" bg={avalCardBg}>
                                <Card.Body p={4}>
                                  <HStack gap={4}>
                                    <Avatar.Root size="md" bg="green.100" color="green.600"><Avatar.Fallback name={aval.cofradia?.nombre}>{<Icon as={FaChurch} />}</Avatar.Fallback><Avatar.Image src={aval.cofradia?.avatar} /></Avatar.Root>
                                    <VStack align="start" gap={1} flex={1}>
                                      <HStack>
                                        <Text fontWeight="bold" color={headingStrongColor}>
                                          {aval.cofradia?.nombre}
                                        </Text>
                                        <Icon as={FaCheckCircle} color="green.500" w={4} h={4} />
                                      </HStack>
                                      {aval.texto && (
                                        <Text fontSize="sm" color={textColor} fontStyle="italic">
                                          "{aval.texto}"
                                        </Text>
                                      )}
                                      <Text fontSize="xs" color={mutedColor}>
                                        Avalado desde {new Date(aval.acceptedAt).toLocaleDateString('es-ES', {
                                          month: 'long',
                                          year: 'numeric'
                                        })}
                                      </Text>
                                    </VStack>
                                    <Icon as={FaAward} w={6} h={6} color="green.500" />
                                  </HStack>
                                </Card.Body>
                              </Card.Root>
                            ))}
                            
                            {bandData.avales.length > 5 && (
                              <Box textAlign="center" pt={2}>
                                <Button variant="ghost" colorPalette="green" size="sm">
                                  Ver todos los avales ({bandData.avales.length})
                                </Button>
                              </Box>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                    {/* Reseñas */}
                    {bandData.resenas && bandData.resenas.length > 0 && (
                      <Card.Root>
                        <Card.Header>
                          <Heading size="md">
                            <Icon as={FaStar} mr={2} color="yellow.500" />
                            Reseñas y Valoraciones
                          </Heading>
                          <Text fontSize="sm" color={textColor}>
                            Valoraciones tras eventos completados en la plataforma
                          </Text>
                        </Card.Header>
                        <Card.Body pt={0}>
                          <VStack gap={4} align="stretch">
                            {/* Resumen de valoraciones */}
                            <Card.Root variant="outline" bg={resenasResumenCardBg}>
                              <Card.Body p={4}>
                                <HStack gap={6} justify="center">
                                  <VStack gap={1}>
                                    <HStack gap={1}>
                                      <Text fontSize="3xl" fontWeight="bold" color="purple.600">{estadisticasResenas.promedio}</Text>
                                      <Icon as={FaStar} color="yellow.500" w={6} h={6} />
                                    </HStack>
                                    <Text fontSize="sm" color={textColor}>Valoración media</Text>
                                  </VStack>
                                  <VStack gap={1}>
                                    <Text fontSize="2xl" fontWeight="bold" color="purple.600">{estadisticasResenas.total}</Text>
                                    <Text fontSize="sm" color={textColor}>Reseñas totales</Text>
                                  </VStack>
                                </HStack>
                              </Card.Body>
                            </Card.Root>

                            {/* Reseñas individuales */}
                            {bandData.resenas.slice(0, 3).map((resena) => (
                              <Card.Root key={resena.id} variant="outline" bg={cardBg}>
                                <Card.Body p={4}>
                                  <VStack gap={3} align="stretch">
                                    {/* Header de la reseña */}
                                    <HStack gap={3} justify="space-between">
                                      <HStack gap={3}>
                                        <Avatar.Root size="sm" bg="purple.100" color="purple.600"><Avatar.Fallback name={resena.cofradiaName || resena.autor}>{<Icon as={FaChurch} />}</Avatar.Fallback><Avatar.Image src={resena.cofradiaAvatar || resena.avatar} /></Avatar.Root>
                                        <VStack align="start" gap={0}>
                                          <Text fontSize="sm" fontWeight="medium" color={headingStrongColor}>
                                            {resena.cofradiaName || resena.autor}
                                          </Text>
                                          <Text fontSize="xs" color={textColor}>
                                            {resena.eventoNombre && `${resena.eventoNombre} • `}
                                            {new Date(resena.receivedDate || resena.fecha).toLocaleDateString('es-ES', {
                                              year: 'numeric',
                                              month: 'long',
                                              day: 'numeric'
                                            })}
                                          </Text>
                                        </VStack>
                                      </HStack>
                                      
                                      {/* Rating */}
                                      <HStack gap={1}>
                                        {[...Array(5)].map((_, i) => (
                                          <Icon
                                            key={i}
                                            as={i < (resena.ratingGeneral || resena.rating) ? FaStar : FaRegStar}
                                            w={4}
                                            h={4}
                                            color={i < (resena.ratingGeneral || resena.rating) ? "yellow.500" : "gray.300"}
                                          />
                                        ))}
                                      </HStack>
                                    </HStack>
                                    
                                    {/* Comentario */}
                                    {(resena.comment || resena.comentario) && (
                                      <Text fontSize="sm" color={textColor} lineHeight="tall" fontStyle="italic">
                                        "{resena.comment || resena.comentario}"
                                      </Text>
                                    )}
                                  </VStack>
                                </Card.Body>
                              </Card.Root>
                            ))}

                            {/* Botón para ver más reseñas */}
                            {bandData.resenas.length > 3 && (
                              <Box textAlign="center" pt={2}>
                                <Button
                                  variant="ghost"
                                  colorPalette="purple"
                                  size="sm"
                                >
                                  Ver todas las reseñas ({bandData.resenas.length})
                                </Button>
                              </Box>
                            )}
                          </VStack>
                        </Card.Body>
                      </Card.Root>
                    )}

                  </VStack>
                </Container>
              </Box>
            </Dialog.Body>

            <Dialog.Footer>
              <HStack gap={3}>
                <Text fontSize="sm" color={mutedColor}>
                  Así verán tu perfil las cofradías
                </Text>
                <Button variant="ghost" onClick={onClose}>
                  Cerrar
                </Button>
              </HStack>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

        {/* Modal para ver imagen en grande */}
        <Dialog.Root open={isImageModalOpen} size='full' placement='center' onOpenChange={e => {
          if (!e.open) {
            handleCloseImageModal();
          }
        }}>
          <Portal>

            <Dialog.Backdrop bg="blackAlpha.800" />
            <Dialog.Positioner>
              <Dialog.Content bg="transparent" boxShadow="none">
                <Dialog.CloseTrigger
                  color="white"
                  size="lg"
                  bg="blackAlpha.600"
                  _hover={{ bg: 'blackAlpha.700' }}
                  rounded="full"
                  m={4}
                />
                <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4}>
                  {selectedImage && (
                    <Image
                      src={selectedImage}
                      alt="Imagen en tamaño completo"
                      maxH="90vh"
                      maxW="90vw"
                      objectFit="contain"
                    />
                  )}
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>

          </Portal>
        </Dialog.Root>

      </Portal>
    </Dialog.Root>
  );
};

export default PublicProfilePreview;
